import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import crmApi, { money } from '../../utils/crmApi';
import useLiveRefresh from '../../utils/useLiveRefresh';

const METRICS = [
  { key: 'revenue', label: 'Revenue (sales value)', money: true },
  { key: 'margin', label: 'Gross margin', money: true },
  { key: 'deals_won_value', label: 'Won deal value', money: true },
  { key: 'product_units', label: 'Units sold (of a product)', money: false, needsProduct: true },
  { key: 'deals_won', label: 'Deals won', money: false },
  { key: 'service_orders', label: 'Service orders (approved)', money: false },
  { key: 'leads', label: 'Leads created', money: false },
  { key: 'quotes_sent', label: 'Quotes sent', money: false },
  { key: 'new_accounts', label: 'New accounts', money: false },
  { key: 'meetings', label: 'Meetings logged', money: false },
  { key: 'calls', label: 'Calls logged', money: false },
  { key: 'activities', label: 'Activities logged (any)', money: false },
];
const PERIODS = [{ key: 'month', label: 'This month' }, { key: 'quarter', label: 'This quarter' }, { key: 'year', label: 'This year' }, { key: 'custom', label: 'Custom dates' }];

function ringColor(pct) { return pct >= 80 ? '#16A34A' : pct >= 50 ? '#D97706' : '#DC2626'; }

function Ring({ pct, size = 64 }) {
  const r = (size - 8) / 2, c = 2 * Math.PI * r, off = c - (pct / 100) * c;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface2)" strokeWidth="6" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={ringColor(pct)} strokeWidth="6" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />
    </svg>
  );
}

export default function CrmGoals() {
  const { isAdmin } = useOutletContext();
  const [goals, setGoals] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState({ title: '', metric: 'revenue', target: '', period: 'month', scope_type: 'company', scope_user_id: '', scope_product_id: '', start_date: '', end_date: '' });
  const [expanded, setExpanded] = useState(null);
  const [contribs, setContribs] = useState({});

  async function toggleContrib(g) {
    if (expanded === g.id) { setExpanded(null); return; }
    setExpanded(g.id);
    if (!contribs[g.id]) {
      try { const r = await crmApi.get(`/goals/${g.id}/contributions`); setContribs(prev => ({ ...prev, [g.id]: r })); }
      catch (e) { setContribs(prev => ({ ...prev, [g.id]: { contributions: [] } })); }
    }
  }

  function load(silent) {
    if (!silent) setLoading(true);
    crmApi.get('/goals').then(setGoals).finally(() => { if (!silent) setLoading(false); });
  }
  useEffect(() => { load(); }, []);
  useLiveRefresh(() => load(true));
  useEffect(() => {
    crmApi.get('/products').then(p => setProducts(Array.isArray(p) ? p : [])).catch(() => {});
    crmApi.get('/users').then(u => setUsers(Array.isArray(u) ? u : [])).catch(() => {});
  }, []);

  const metric = METRICS.find(m => m.key === form.metric) || METRICS[0];

  function openNew() {
    setEditingId(null);
    setForm({ title: '', metric: 'revenue', target: '', period: 'month', scope_type: 'company', scope_user_id: '', scope_product_id: '', start_date: '', end_date: '' });
    setShow(true);
  }
  function openEdit(g) {
    setEditingId(g.id);
    setForm({ title: g.title || '', metric: g.metric, target: g.target, period: g.period,
      scope_type: g.scope_type || 'company', scope_user_id: g.scope_user_id || '', scope_product_id: g.scope_product_id || '',
      start_date: g.start_date || '', end_date: g.end_date || '' });
    setShow(true);
  }

  async function save() {
    if (!form.target) return;
    const payload = { ...form, target: parseFloat(form.target) || 0 };
    if (form.metric === 'product_units') payload.scope_type = 'product';
    if (editingId) await crmApi.put(`/goals/${editingId}`, payload);
    else await crmApi.post('/goals', payload);
    setShow(false); setEditingId(null);
    setForm({ title: '', metric: 'revenue', target: '', period: 'month', scope_type: 'company', scope_user_id: '', scope_product_id: '', start_date: '', end_date: '' });
    load();
  }
  async function doDelete() {
    if (!confirmDel) return;
    await crmApi.delete(`/goals/${confirmDel.id}`); setConfirmDel(null); load();
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: '800' }}>Goals & Targets</h1>
          <p style={{ fontSize: '13px', color: 'var(--muted)' }}>Set targets for the team, a person, or a product. Progress is tracked automatically and shown on the dashboard.</p>
        </div>
        {isAdmin && <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={openNew}>+ New Goal</button>}
      </div>

      {loading ? <div style={{ color: 'var(--muted)' }}>Loading…</div> :
        goals.length === 0 ? <div className="card" style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>No goals yet. {isAdmin ? 'Create one to start tracking targets.' : ''}</div> :
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {goals.map(g => {
              const cdata = contribs[g.id];
              const isOpen = expanded === g.id;
              const maxAmt = cdata && cdata.contributions.length ? Math.max(...cdata.contributions.map(x => x.amount || 0), 1) : 1;
              return (
                <div key={g.id} className="card" style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <Ring pct={g.pct} />
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '800' }}>{g.pct}%</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '800' }}>{g.title || g.metric_label}</div>
                      <div style={{ fontSize: '12px', color: 'var(--muted2)', marginTop: '2px' }}>{g.is_money ? money(g.actual) : g.actual} / {g.is_money ? money(g.target) : g.target}</div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '3px' }}>
                        {g.metric_label} · {g.scope_label} · {g.period === 'custom' && g.start_date ? `${g.start_date} → ${g.end_date}` : g.period}
                        {g.status === 'closed' ? ' · ' : ` · ${g.days_left}d left`}
                        {g.status === 'closed' && <span style={{ color: g.achieved ? '#16A34A' : '#DC2626', fontWeight: 700 }}>{g.achieved ? 'Closed · Achieved ✓' : 'Closed · Missed'}</span>}
                      </div>
                    </div>
                    <button className="btn btn-sm" onClick={() => toggleContrib(g)}>{isOpen ? 'Hide' : 'Contributions'}</button>
                    {isAdmin && <button className="btn btn-sm" onClick={() => openEdit(g)}>Edit</button>}
                    {isAdmin && <button className="btn btn-sm" onClick={() => setConfirmDel(g)} style={{ color: '#DC2626' }}>Delete</button>}
                  </div>

                  {isOpen && (
                    <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid var(--border)' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '10px' }}>Contribution by person</div>
                      {!cdata ? <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Loading…</div> :
                        cdata.contributions.length === 0 ? <div style={{ fontSize: '12px', color: 'var(--muted)' }}>No contributions recorded in this period yet.</div> :
                          cdata.contributions.map(p => (
                            <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                              <div style={{ fontSize: '12px', fontWeight: '600', width: '140px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                              <div style={{ flex: 1, height: '10px', background: 'var(--surface2)', borderRadius: '99px', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${((p.amount || 0) / maxAmt) * 100}%`, background: '#0D9488', borderRadius: '99px' }} />
                              </div>
                              <div style={{ fontSize: '12px', fontWeight: '700', width: '90px', textAlign: 'right' }}>{cdata.is_money ? money(p.amount) : p.amount}</div>
                              <div style={{ fontSize: '11px', color: 'var(--muted)', width: '70px', textAlign: 'right' }}>{p.activities} actions</div>
                            </div>
                          ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>}

      {show && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShow(false); }}>
          <div className="modal" style={{ width: '460px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '16px' }}>{editingId ? 'Edit Goal' : 'New Goal'}</h3>
            <div className="form-group"><label className="form-label">Title <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(optional)</span></label>
              <input className="input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Q3 revenue push" /></div>
            <div className="form-group"><label className="form-label">What to measure</label>
              <select className="select" value={form.metric} onChange={e => setForm({ ...form, metric: e.target.value })}>
                {METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select></div>
            {metric.needsProduct && (
              <div className="form-group"><label className="form-label">Product</label>
                <select className="select" value={form.scope_product_id} onChange={e => setForm({ ...form, scope_product_id: e.target.value })}>
                  <option value="">Select product…</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <div className="form-group" style={{ flex: 1 }}><label className="form-label">Target {metric.money ? '(amount)' : '(number)'}</label>
                <input className="input" type="number" value={form.target} onChange={e => setForm({ ...form, target: e.target.value })} /></div>
              <div className="form-group" style={{ flex: 1 }}><label className="form-label">Period</label>
                <select className="select" value={form.period} onChange={e => setForm({ ...form, period: e.target.value })}>
                  {PERIODS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select></div>
            </div>
            {form.period === 'custom' && (
              <div style={{ display: 'flex', gap: '12px' }}>
                <div className="form-group" style={{ flex: 1 }}><label className="form-label">Start date</label>
                  <input className="input" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
                <div className="form-group" style={{ flex: 1 }}><label className="form-label">End date (auto-closes after)</label>
                  <input className="input" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
              </div>
            )}
            {!metric.needsProduct && (
              <div className="form-group"><label className="form-label">Applies to</label>
                <select className="select" value={form.scope_type === 'user' ? `user:${form.scope_user_id}` : 'company'}
                  onChange={e => {
                    if (e.target.value === 'company') setForm({ ...form, scope_type: 'company', scope_user_id: '' });
                    else setForm({ ...form, scope_type: 'user', scope_user_id: e.target.value.split(':')[1] });
                  }}>
                  <option value="company">Whole company</option>
                  {users.map(u => <option key={u.id} value={`user:${u.id}`}>{u.full_name}</option>)}
                </select></div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
              <button className="btn" onClick={() => setShow(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>{editingId ? 'Save Changes' : 'Create Goal'}</button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setConfirmDel(null); }}>
          <div className="modal" style={{ width: '380px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '800', marginBottom: '8px' }}>Delete goal?</h3>
            <p style={{ fontSize: '13px', color: 'var(--muted2)', lineHeight: 1.6, marginBottom: '14px' }}>
              This will remove the goal <strong>{confirmDel.title || confirmDel.metric_label}</strong> and stop tracking its progress. This can't be undone.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="btn" style={{ background: '#DC2626', color: '#fff', borderColor: '#DC2626' }} onClick={doDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
